import { useIsRestoring } from "@tanstack/react-query";
import { PersistQueryClientProvider } from "@tanstack/react-query-persist-client";
import { PropsWithChildren } from "react";
import { QUERY_CLIENT_CACHE_MAX_AGE, queryClient } from "../api";
import { queryPersister } from "./query-persister";

export function QueryProvider({ children }: PropsWithChildren) {
  return (
    <PersistQueryClientProvider
      client={queryClient}
      persistOptions={{
        persister: queryPersister,
        // For difference between gcTime and maxAge see https://github.com/TanStack/query/discussions/5169#discussioncomment-11686216
        maxAge: QUERY_CLIENT_CACHE_MAX_AGE,
      }}
    >
      <PersistGate>{children}</PersistGate>
    </PersistQueryClientProvider>
  );
}

/**
 * This component is used to prevent the app from rendering before the persisted cache is restored.
 * This is necessary to ensure that fetched data is stored reliably in the persisted cache.
 *
 * Without this gate, the following can happen:
 * - PersistQueryClientProvider starts restoring the cache
 * - Data is fetched
 * - Data is retrieved before the cache is restored
 * - Data is not stored in the cache, because it's not ready yet (setItem is not called)
 * - PersistQueryClientProvider is done restoring the cache
 * - Data will be cached on subsequent queries, but the initial data is not cached
 * - If the user would reload the app in an offline scenario now, there would be no data
 *
 * Another option would be to invalidate all queries in onSuccess handler of PersistQueryClientProvider, thus triggering a refetch.
 */
function PersistGate({ children }: PropsWithChildren) {
  // from https://github.com/TanStack/query/issues/6148#issuecomment-1761263234
  const isRestoring = useIsRestoring();
  return isRestoring ? null : children;
}
